/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Disclosure } from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { type CoachSuggestionContentComponent } from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionContent';
import { type CoachSuggestionFooterComponent } from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionFooter';
import { type CoachSuggestionHeaderComponent } from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionHeader';
import { CoachSuggestionContext } from 'bundles/ai-coach-platform/components/patterns/suggestions/context/coachSuggestionContext';
import { useCoachSuggestionsContext } from 'bundles/ai-coach-platform/components/patterns/suggestions/context/coachSuggestionsContext';
import {
  ResolutionStatus,
  type SuggestionAction,
} from 'bundles/ai-coach-platform/components/patterns/suggestions/types';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  coachSuggestion: (expanded = false) => css`
    display: block;

    .disclosure {
      background-color: var(--cds-color-neutral-background-primary-weak) !important;
      transition: background-color 0.3s;
      border-radius: 0 !important;

      ${expanded && `background-color: var(--cds-color-emphasis-primary-background-weak) !important;`}

      .disclosure-content {
        max-height: 200px;
        overflow: auto;
        color: var(--cds-color-grey-800);
      }

      .disclosure-header {
        border-radius: 0 !important;
        padding: var(--cds-spacing-200) !important;
      }

      .disclosure-content,
      .disclosure-footer {
        padding-left: var(--cds-spacing-200) !important;
      }
    }
  `,
};

type Props = {
  id: string;
  status: ResolutionStatus;
  /**
   * Pass this for custom cases where the visual title is not a string
   * e.g. the title is composed of multiple DOM elements
   */
  ariaLabel?: string;

  onAction?: (action: SuggestionAction) => void;
  onToggle?: (expanded: boolean) => void;

  className?: string;

  disabled?: boolean;

  /** When false, the suggestion stays expanded until the caller collapses it (e.g. after async confirm). */
  collapseOnAction?: boolean;

  children: [CoachSuggestionHeaderComponent, CoachSuggestionContentComponent, CoachSuggestionFooterComponent];
};

export type CoachSuggestionComponent = React.ReactElement<Props>;

export const CoachSuggestion = (props: Props) => {
  const { expandedId, onDisclosureToggle, getIndex, onAction, updateSuggestionRef } = useCoachSuggestionsContext();
  const {
    id,
    ariaLabel = _t('Suggestion #{suggestionNumber}', { suggestionNumber: getIndex(id) + 1 }),
    status,
    className,
    onAction: onUserAction,
    onToggle,
    disabled,
    collapseOnAction = true,
    children,
  } = props;

  const isExpanded = expandedId === id;

  const handleToggle = React.useCallback<() => void>(() => {
    onToggle?.(!isExpanded);
    onDisclosureToggle(id, !isExpanded);
  }, [isExpanded, onDisclosureToggle, onToggle, id]);

  const handleAction = React.useCallback<(action: SuggestionAction) => Promise<void>>(
    async (action) => {
      try {
        await onAction?.(id, action);
      } catch {
        // CoachSuggestionFooter fires this without awaiting or catching it, so swallow here to avoid
        // an unhandled rejection. This doesn't hide the failure: anything that needs to react to it
        // (e.g. not advancing to the next suggestion) already ran before the rejection reached here.
        return;
      }
      onUserAction?.(action);
    },
    [id, onAction, onUserAction]
  );

  const isResolved = status !== ResolutionStatus.UNRESOLVED;
  const isDisabled = disabled || isResolved;
  const [renderHeader, renderContent, renderFooter] = children;

  return (
    <CoachSuggestionContext.Provider
      value={{ id, status, isExpanded, collapseOnAction, onAction: handleAction, onToggle: handleToggle }}
    >
      <div
        css={styles.coachSuggestion(isExpanded)}
        className={classNames('coach-suggestion', className)}
        aria-disabled={isDisabled}
      >
        <Disclosure
          collapsible={!isDisabled} // collapsed and read-only once resolved
          expanded={isExpanded} // allows us to auto-toggle each disclosure
          defaultExpanded={false} // all suggestions collapsed by default, but controlled by `isExpanded`
          onToggle={handleToggle}
          header={renderHeader}
          aria-label={`${ariaLabel}, ${status}`}
          footer={renderFooter}
          ref={(ref) => updateSuggestionRef(id, ref)}
        >
          {renderContent}
        </Disclosure>
      </div>
    </CoachSuggestionContext.Provider>
  );
};

export default CoachSuggestion;
